import { useRef, useState } from 'react'
import { formatCode } from '../../guests'

interface InviteLinkProps {
  code: string
}

/** How long the acknowledgement stays up. */
const CONFIRM_MS = 1800

type State = 'idle' | 'copied' | 'failed'

/**
 * Copies an invite's direct link to the clipboard.
 *
 * The URL is not shown: it is only ever the origin plus the code, and the code is
 * already right beside this. Revealing it would be a second copy of something the
 * couple can read anyway.
 *
 * Two fallbacks, because `navigator.clipboard` cannot be relied on - it wants a
 * secure context and permission, and hardened or embedded browsers refuse it
 * outright, which is the same lesson as `window.confirm` next door in the delete
 * confirmation. If the modern API is refused it goes through a scratch textarea
 * and `execCommand`, and only if both fail does it give up and show the link for
 * copying by hand.
 */
export function InviteLink({ code }: InviteLinkProps) {
  const [state, setState] = useState<State>('idle')
  const timer = useRef(0)

  // Read at click time, so the link is right whether the console is open on the
  // live domain or on localhost.
  const url = () => `${window.location.origin}/${code}`

  async function copy() {
    window.clearTimeout(timer.current)
    setState((await writeToClipboard(url())) ? 'copied' : 'failed')
    timer.current = window.setTimeout(() => setState('idle'), CONFIRM_MS)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={`Copy the link for invite ${formatCode(code)}`}
        title="Copy link"
        className="border-ink/20 hover:border-claret hover:text-claret text-ink-soft rounded-md border p-1.5 transition-colors"
      >
        {state === 'copied' ? <TickIcon /> : <ClipboardIcon />}
      </button>

      {/* Announced rather than drawn, so the row does not change width as it
          appears. The failure is worth showing, since the link is then the only
          way to get at it. */}
      <span role="status" className="sr-only">
        {state === 'copied' ? 'Link copied' : ''}
      </span>
      {state === 'failed' && (
        <span className="text-ink-soft basis-full font-mono text-xs break-all">
          Could not reach the clipboard. The link is {url()}
        </span>
      )}
    </>
  )
}

/** True when the link made it to the clipboard, by whichever route. */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Carry on to the old way.
  }

  const scratch = document.createElement('textarea')
  scratch.value = text
  // Off screen rather than hidden: a display:none element cannot be selected.
  scratch.setAttribute('aria-hidden', 'true')
  scratch.style.cssText = 'position:fixed;top:-1000px;opacity:0'
  document.body.appendChild(scratch)
  try {
    scratch.select()
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    scratch.remove()
  }
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="9" y="9" width="11" height="12" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h8" />
    </svg>
  )
}

function TickIcon() {
  return (
    <svg viewBox="0 0 24 24" className="text-sage h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
